import { Link } from 'react-router-dom';
import { Building2, MapPin, Loader2, ArrowRightCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { Company, CompanyType } from '@/modules/core/companies/types/company.types';
import { COMPANY_TYPE_CONFIG } from '@/modules/core/companies/types/company.types';

interface CompaniesKanbanProps {
  companies: Company[];
  isLoading?: boolean;
  onConvertToClient?: (company: Company) => void;
}

const COLUMNS: { type: CompanyType; title: string }[] = [
  { type: 'prospect', title: 'Prospects' },
  { type: 'client', title: 'Clients' },
];

export function CompaniesKanban({
  companies,
  isLoading,
  onConvertToClient,
}: CompaniesKanbanProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-copper" />
      </div>
    );
  }

  const grouped = COLUMNS.map((col) => ({
    ...col,
    companies: companies.filter((c) => c.companyType === col.type),
  }));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {grouped.map((column) => {
        const config = COMPANY_TYPE_CONFIG[column.type];
        return (
          <div
            key={column.type}
            className="bg-muted/30 rounded-lg p-3 min-h-[320px]"
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{column.title}</h3>
                <Badge variant="outline" className="text-xs">
                  {column.companies.length}
                </Badge>
              </div>
            </div>
            <div className="space-y-2">
              {column.companies.length === 0 && (
                <div className="text-center text-sm text-muted-foreground py-8">
                  Aucune société
                </div>
              )}
              {column.companies.map((company) => {
                const oppCount = company.opportunitiesCount ?? 0;
                return (
                  <Card
                    key={company.id}
                    className="hover:shadow-md transition-shadow"
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <Link
                          to={`/tm/companies/${company.id}`}
                          className="flex items-start gap-2 min-w-0 flex-1 group"
                        >
                          <div className="p-1.5 bg-muted rounded shrink-0">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-medium text-sm truncate group-hover:text-copper">
                              {company.name}
                            </h4>
                            {company.city && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                <MapPin className="h-3 w-3" />
                                {company.city}
                              </p>
                            )}
                          </div>
                        </Link>
                        <Badge
                          className={`text-xs shrink-0 ${config.bgColor} ${config.color}`}
                        >
                          {oppCount} opp.
                        </Badge>
                      </div>
                      {column.type === 'prospect' && onConvertToClient && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full mt-2 h-7 text-xs"
                          onClick={() => onConvertToClient(company)}
                        >
                          <ArrowRightCircle className="h-3 w-3 mr-1" />
                          Passer en client
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
